import React, { useEffect, useRef, useState } from 'react'
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import type { SupportCenterClientOption } from '../../types/supportCenterReports'

const DEBOUNCE_MS = 300

type ClientComboboxProps = {
  value: string | null
  initialClient?: { name: string; id_scgp: number | null } | null
  onChange: (id: string | null, client: SupportCenterClientOption | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /**
   * Quando informado, filtra só nesta lista (sem chamar a API).
   * Ex.: clientes que já têm atendimento no dashboard.
   */
  presetOptions?: SupportCenterClientOption[]
}

function clientOptionLabel(client: { name: string; id_scgp: number | null }): string {
  const name = client.name?.trim() || '—'
  if (client.id_scgp == null) return name
  return `${client.id_scgp} - ${name}`
}

export const ClientCombobox: React.FC<ClientComboboxProps> = ({
  value,
  initialClient,
  onChange,
  placeholder = 'Digite o código SCGP ou nome do cliente',
  disabled = false,
  className = '',
  presetOptions,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SupportCenterClientOption[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const usePreset = presetOptions != null

  const selectedLabel =
    value && initialClient ? clientOptionLabel(initialClient) : ''

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!open) {
      setResults([])
      setLoading(false)
      return
    }

    if (usePreset) {
      const q = query.trim().toLowerCase()
      const filtered = !q
        ? presetOptions
        : presetOptions.filter((client) => {
            const name = (client.name ?? '').toLowerCase()
            const code = client.id_scgp != null ? String(client.id_scgp) : ''
            return name.includes(q) || code.includes(q)
          })
      setResults(filtered)
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await supportCenterReportsService.searchClients(query)
        setResults(data)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, open, usePreset, presetOptions])

  const handleSelect = (client: SupportCenterClientOption | null) => {
    onChange(client?.id ?? null, client)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null, null)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const inputDisplayValue = open ? query : selectedLabel
  const showDropdown = open

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={inputDisplayValue}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            setQuery('')
            setOpen(true)
          }}
          onBlur={() => {
            setTimeout(() => {
              if (!containerRef.current?.contains(document.activeElement)) {
                setOpen(false)
                setQuery('')
              }
            }, 150)
          }}
          disabled={disabled}
          placeholder={value ? undefined : placeholder}
          autoComplete="off"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 pr-16 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-1 gap-0.5">
          {loading && (
            <svg
              className="animate-spin h-3.5 w-3.5 text-primary-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          )}
          {value && !disabled && !loading && (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Remover cliente"
              tabIndex={-1}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              if (open) setOpen(false)
              else setOpen(true)
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronUpDownIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showDropdown && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg text-sm focus:outline-none">
          <li
            onMouseDown={(e) => {
              e.preventDefault()
              handleSelect(null)
            }}
            className="cursor-pointer select-none px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 border-b border-gray-100 dark:border-gray-700"
          >
            - Nenhum -
          </li>

          {loading ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none">
              Buscando...
            </li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none">
              Nenhum cliente encontrado.
            </li>
          ) : (
            results.map((client) => (
              <li
                key={client.id}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(client)
                }}
                className={`cursor-pointer select-none px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 ${
                  client.id === value
                    ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/60 dark:bg-primary-900/20'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {clientOptionLabel(client)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

export default ClientCombobox
